import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardEyebrow, CardTitle, CardContent } from '@bookrcs/ui';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import type { Appointment } from '@bookrcs/types';

const FR_DAYS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'] as const;

function startOfWeek(d: Date): Date {
  const x = new Date(d);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfWeek(d: Date): Date {
  const start = startOfWeek(d);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  end.setMilliseconds(-1);
  return end;
}

function formatTime(d: Date | string): string {
  const x = typeof d === 'string' ? new Date(d) : d;
  return x.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export default function CalendarRoute() {
  const { session } = useAuth();
  const today = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => startOfWeek(today), [today]);
  const weekEnd = useMemo(() => endOfWeek(today), [today]);

  const {
    data: appointments,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['appointments', session?.proId, 'week', weekStart.toISOString()],
    queryFn: () =>
      api<Appointment[]>('/appointments', {
        searchParams: {
          proId: session?.proId ?? '',
          from: weekStart.toISOString(),
          to: weekEnd.toISOString(),
        },
      }),
    enabled: Boolean(session?.proId),
  });

  const byDay = useMemo(() => {
    const buckets: Record<number, Appointment[]> = {
      0: [],
      1: [],
      2: [],
      3: [],
      4: [],
      5: [],
      6: [],
    };
    for (const apt of appointments ?? []) {
      const d = new Date(apt.startAt);
      buckets[d.getDay()]?.push(apt);
    }
    for (const key of Object.keys(buckets)) {
      buckets[Number(key)]?.sort(
        (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
      );
    }
    return buckets;
  }, [appointments]);

  const orderedDays: ReadonlyArray<{ idx: number; label: string; date: Date }> = useMemo(() => {
    const out: Array<{ idx: number; label: string; date: Date }> = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      out.push({ idx: d.getDay(), label: FR_DAYS[d.getDay()] ?? '', date: d });
    }
    return out;
  }, [weekStart]);

  return (
    <div className="grid gap-6 max-w-5xl">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-[var(--text-muted)] m-0">Calendrier</p>
          <h1 className="font-serif text-4xl md:text-5xl font-extrabold m-0 mt-1 text-[var(--text)]">
            Semaine du {weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
          </h1>
        </div>
        <span className="font-mono text-sm text-[var(--text-faint)]">
          {appointments?.length ?? 0} RDV
        </span>
      </header>

      <Card>
        <CardEyebrow>Vue semaine</CardEyebrow>
        <CardTitle>7 jours</CardTitle>
        <CardContent>
          {isLoading ? <p className="text-[var(--text-muted)]">Chargement…</p> : null}
          {isError ? (
            <p className="text-[var(--urgent)]">
              L'API ne répond pas. Vérifie `@bookrcs/api` (port 4000).
            </p>
          ) : null}
          {appointments && appointments.length === 0 ? (
            <p className="text-[var(--text-muted)] italic m-0 mt-2">
              Semaine vide. Profite, ou pousse ton lien Insta.
            </p>
          ) : null}
          {appointments && appointments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-7 gap-2 mt-2">
              {orderedDays.map(({ idx, label, date }) => (
                <div
                  key={date.toISOString()}
                  className="border border-[var(--border)] rounded-[var(--radius-card)] p-2 bg-[var(--elevated)] min-h-[120px]"
                >
                  <div className="font-bold text-sm text-[var(--text-muted)]">
                    {label.slice(0, 3)} <span className="font-mono">{date.getDate()}</span>
                  </div>
                  <ul className="list-none p-0 m-0 mt-2 grid gap-1">
                    {(byDay[idx] ?? []).map((apt) => (
                      <li
                        key={apt.id}
                        className="text-xs p-1 rounded bg-[var(--surface)] border border-[var(--border)] grid gap-0.5"
                      >
                        <time className="font-mono text-[var(--text)]">
                          {formatTime(apt.startAt)}
                        </time>
                        <span className="text-[var(--text-muted)] truncate">{apt.status}</span>
                      </li>
                    ))}
                    {(byDay[idx] ?? []).length === 0 ? (
                      <li className="text-xs text-[var(--text-faint)] italic">vide</li>
                    ) : null}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
